import { Injectable } from '@angular/core';

import { CallableOrderItem } from '../models/ecall-off-model';

interface RddMode {
	/**
	 * Key string to identify the RDDC mode
	 */
	key: string;
	/**
	 * True if the date from is available for that RDDC mode
	 */
	canEditDateFrom(): boolean;
	/**
	 * True if the date to is available for that RDDC mode
	 */
	canEditDateTo(): boolean;
	/**
	 * Update order item dates based on the RDDC mode
	 * @param orderItem - order item to update
	 * @param requestedDate - for mode with only one date, sometime the field is mutualized in one separated field
	 * @param fromDate - from Date
	 * @param toDate - to Date
	 */
	updateDates(orderItem: CallableOrderItem, requestedDate?: Date, fromDate?: Date, toDate?: Date): void;
	/**
	 * Test if the dates are valid for that RDDC mode
	 * @param orderItem - order item to test
	 */
	areDatesValid(orderItem: CallableOrderItem): boolean;
}

const lastestRddMode: RddMode = {
	key: 'LATEST',
	canEditDateFrom: () => false,
	canEditDateTo: () => true,
	updateDates: (orderItem, requestedDate, _fromDate, toDate) => {
		orderItem.requestedDeliveryDateFrom = undefined;
		orderItem.requestedDeliveryDateTo = requestedDate ?? toDate;
	},
	areDatesValid: orderItem => orderItem.requestedDeliveryDateFrom === undefined
		&& orderItem.requestedDeliveryDateTo !== undefined
};

const earliestRddMode: RddMode = {
	key: 'EARLIEST',
	canEditDateFrom: () => true,
	canEditDateTo: () => false,
	updateDates: (orderItem, requestedDate, fromDate, _toDate) => {
		orderItem.requestedDeliveryDateFrom = requestedDate ?? fromDate;
		orderItem.requestedDeliveryDateTo = undefined;
	},
	areDatesValid: orderItem => orderItem.requestedDeliveryDateFrom !== undefined
		&& orderItem.requestedDeliveryDateTo === undefined
};

const exactRddMode: RddMode = {
	key: 'EXACT',
	canEditDateFrom: () => true,
	canEditDateTo: () => false,
	updateDates: (orderItem, requestedDate, fromDate, _toDate) => {
		orderItem.requestedDeliveryDateFrom = requestedDate ?? fromDate;
		orderItem.requestedDeliveryDateTo = requestedDate ?? fromDate;
	},
	areDatesValid: orderItem => orderItem.requestedDeliveryDateFrom !== undefined
		&& orderItem.requestedDeliveryDateTo !== undefined
		&& orderItem.requestedDeliveryDateTo === orderItem.requestedDeliveryDateFrom
};

const asapRddMode: RddMode = {
	key: 'ASAP',
	canEditDateFrom: () => false,
	canEditDateTo: () => false,
	updateDates: (orderItem, _requestedDate, _fromDate, _toDate) => {
		orderItem.requestedDeliveryDateFrom = undefined;
		orderItem.requestedDeliveryDateTo = undefined;
	},
	areDatesValid: orderItem => orderItem.requestedDeliveryDateFrom === undefined
		&& orderItem.requestedDeliveryDateTo === undefined
};

const betweenRddMode: RddMode = {
	key: 'BETWEEN',
	canEditDateFrom: () => true,
	canEditDateTo: () => true,
	updateDates: (orderItem, _requestedDate, fromDate, toDate) => {
		orderItem.requestedDeliveryDateFrom = fromDate;
		orderItem.requestedDeliveryDateTo = toDate;
	},
	areDatesValid: orderItem => orderItem.requestedDeliveryDateFrom !== undefined
		&& orderItem.requestedDeliveryDateTo !== undefined
};

const rddModes = [asapRddMode, betweenRddMode, earliestRddMode, lastestRddMode, exactRddMode];

@Injectable({ providedIn: 'root' })
export class RddModeService {
	/**
	 * Get the RDDC mod from its key
	 * @param key - RDDC mode identifier
	 * @returns the RDDC mode
	 * @throws Error if RDDC mode is not found
	 */
	getRddMode(key?: string): RddMode {
		const rddMode = rddModes.find(mode => mode.key === key?.toUpperCase());
		if (!rddMode) {
			throw new Error(`RDDC mode [${ key ?? '' }] is invalid`);
		}
		return rddMode;
	}

	/**
	 * Check if the RDDC mode exists
	 * @param key - RDDC mode identifier
	 * @returns true if the RDDC mode exists
	 */
	isValidRddMode(key?: string): boolean {
		return key !== undefined && rddModes.map(mode => mode.key).includes(key.toUpperCase());
	}
}
